/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/
// @ts-ignore
import { ICartLineItemViewProps, ICartlinesViewProps, ICartViewProps, IOrderSummaryErrors } from '@msdyn365-commerce-modules/cart';
import { IInvoiceSummaryLines, IInvoiceSummaryProps } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    INodeProps,
    ITelemetryContent,
    Node,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    TelemetryConstant,
    Alert
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import { ICartConfig, ICartProps as ICartExtensionProps, ICartResources } from '../definition-extensions/cart.ext.props.autogenerated';
import { getAdvancedNotesAsync, getSupersessionV2Async } from '../../../actions/DataActionExtension.g';
import { IAdvancedNoteEntity } from '../../../actions/DataServiceEntities.g';
import { LinePagination } from './components/line-pagination';
import { AttributeTextValue, CartLine, CommerceProperty, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import getExtensionPropertyValueByKey, { CommercePropertyValueEnum } from '../../../utils/getExtensionPropertyValueByKey';
import { SupersessionKey } from '../models/SupersessionKey';
import { InventoryBreakdownComponent, WarehouseInventoryType } from './components/inventory-breakdown.component';
import {
    AdditionalAltWarehouseDetails,
    buildUpdatedCartLine,
    checkAlternateWarehouseFeatureAccess,
    computeWarehouseStateForLine,
    convertStringToArray,
    getAdditionalAltWarehouseDetails,
    hasSupersessionPreview,
    shouldEnableSecondaryWarehouses,
    upsertAttributeTextValue,
    upsertBoolExtensionProperty,
    WarehouseState
} from '../../../utils/alternateWarehouseHelper';

type OrderSummaryCheckoutProps = {
    props: ICartViewProps & ICartExtensionProps<{}>;
    renderClearCart: React.ReactNode;
    shouldDisplaySpringOrderModal: boolean;
};

type SupersessionMsgProps = {
    props: ICartViewProps & ICartExtensionProps<{}>;
};

type CreditBalanceMsgProps = {
    props: ICartViewProps & ICartExtensionProps<{}>;
};

/**
 * Toggle Bopis Function.
 * @param cartLine -CartLine.
 * @param isBopisSelected -Boolean.
 * @returns Toggle Bopis.
 */
const toggleBopisHandler = (cartLine: ICartlinesViewProps, isBopisSelected: boolean) => () => {
    cartLine.pickUpInStore?.callbacks.toggleBopis(!isBopisSelected);
};

/**
 * Change Store Function.
 * @param cartLine -CartLine.
 * @returns Toggle Bopis.
 */
const onChangeStoreHandler = (cartLine: ICartlinesViewProps) => () => {
    cartLine.pickUpInStore?.callbacks.toggleBopis(true);
};

/**
 * Render Bopis block.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param telemetryContent - The telemetry object.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderBopisBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for BOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const {
        shipInsteadDisplayText,
        shipToAddressDisplayText,
        pickItUpDisplayText,
        pickUpAtStoreWithLocationText,
        changeStoreDisplayText
    } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <Node {...cartLine.pickUpInStore.ContainerProps}>
            <div className='msc-cart-line__bopis-method'>
                {isBopisSelected ? (
                    <span className='pick-up'>{pickUpAtStoreWithLocationText}</span>
                ) : (
                    <span className='ship'>{shipToAddressDisplayText}</span>
                )}
            </div>
            {isBopisSelected && (
                <div className='msc-cart-line__bopis-fullfilment'>
                    <span className='msc-cart-line__bopis-fullfilment-store'>{cartLine.pickUpInStore.orgUnitName}</span>
                    <Button role='link' className='msc-cart-line__bopis-changestore' onClick={onChangeStoreHandler(cartLine)}>
                        {changeStoreDisplayText}
                    </Button>
                </div>
            )}
            <Button
                className='msc-cart-line__bopis-btn'
                {...puckUpinStoreAttribute}
                onClick={toggleBopisHandler(cartLine, isBopisSelected)}
            >
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </Node>
    );
};

/**
 * Render Bopis block.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param telemetryContent - The telemetry object.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderGroupBopisBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for GroupBOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const { changeStoreDisplayText, shipInsteadDisplayText, pickItUpDisplayText } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <>
            {isBopisSelected ? (
                <Button role='link' className='msc-cart-line__remove-item msc-btn' onClick={onChangeStoreHandler(cartLine)}>
                    {changeStoreDisplayText}
                </Button>
            ) : null}
            <Button
                role='link'
                className='msc-cart-line__remove-item msc-btn'
                {...puckUpinStoreAttribute}
                onClick={toggleBopisHandler(cartLine, isBopisSelected)}
            >
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </>
    );
};

const renderCartLineComment = (cartLineComment: React.ReactNode, comment: string, cartLineId: string, cartProductTitle?: string) => {
    if (!cartLineComment) {
        return null;
    }
    // @ts-ignore
    return React.cloneElement(cartLineComment[0] as React.ReactElement, { comment, cartLineId, cartProductTitle });
};

/**
 * Render Cart Lines.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param cartLineComment - Cart line comment element
 * @param telemetryContent - The telemetry object.
 * @param isMultiplePickUp - FLag to check if there multiple pickups.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderCartLine = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    config: ICartConfig,
    storeSelector: React.ReactNode | undefined,
    cartLineComment: React.ReactNode | undefined,
    advancedNotes: React.ReactNode | undefined,
    productExtensionProperties: CommerceProperty[] | undefined,
    telemetryContent?: ITelemetryContent,
    isMultiplePickUp?: boolean,
    emailDeliveryModeCode?: string
): JSX.Element => {
    const comment = cartLine.data?.cartline.Comment || '';
    const cartLineProductTitle = cartLine.data?.product?.Name;
    const diplayMovetowishlist = config.displayMoveToWishlistButton;
    if (cartLine.error === resources.outOfStockErrorMessage) {
        return (
            <Node className='msc-cart-line__actions'>
                {renderCartLineComment(cartLineComment, comment, cartLine.cartlineId!, cartLineProductTitle)}
                {diplayMovetowishlist && cartLine.addToWishlist}
                {cartLine.remove}
            </Node>
        );
    }

    if (isMultiplePickUp) {
        return (
            <Node className='msc-cart-line-group__extra-actions'>
                {renderGroupBopisBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                {renderCartLineComment(cartLineComment, comment, cartLine.cartlineId!, cartLineProductTitle)}
                {cartLine.addToOrderTemplate}
                {diplayMovetowishlist && cartLine.addToWishlist}
                {cartLine.remove}
            </Node>
        );
    }

    if (cartLine.addToOrderTemplate) {
        return (
            <Node className='msc-cart-line__extra-actions'>
                {renderCartLineComment(cartLineComment, comment, cartLine.cartlineId!, cartLineProductTitle)}
                {cartLine.addToOrderTemplate}
                {diplayMovetowishlist && cartLine.addToWishlist}
                {cartLine.remove}
            </Node>
        );
    }

    return (
        <Node className='msc-cart-line__actions'>
            {renderCartLineComment(cartLineComment, comment, cartLine.cartlineId!, cartLineProductTitle)}
            {diplayMovetowishlist && cartLine.addToWishlist}
            {cartLine.remove}
        </Node>
    );
};

/**
 * Render Cart Lines.
 * @param cartLines - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param telemetryContent - The telemetry object.
 * @param isMultiplePickUp - Flag to check if there multiple pickups.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderCartlinesInternal = (
    supersessionData: SimpleProduct[],
    handleLinkClick: (lineId: string) => void,
    notes: IAdvancedNoteEntity[],
    isPrintView: boolean,
    cartLines: ICartlinesViewProps[],
    resources: ICartResources,
    config: ICartConfig,
    storeSelector: React.ReactNode | undefined,
    cartLineComment: React.ReactNode | undefined,
    advancedNotes: React.ReactNode | undefined,
    isAlternateWarehouseSelectionDisabled: boolean,
    shouldDisplayAlternateWarehouseFeature: boolean,
    warehouseStateMap: Map<string, WarehouseState>,
    appConfig: any,
    // enforcePrimaryWarehouseAvailability: boolean,
    // hasWarehouseChanged: boolean | undefined,
    telemetryContent?: ITelemetryContent,
    isMultiplePickUp?: boolean,
    emailDeliveryModeCode?: string
): JSX.Element[] | JSX.Element => {
    return cartLines.map((cartLine, index) => {
        const itemIndex = index;
        const itemId = cartLine.data?.product?.ItemId || '';
        const productExtensionProperties = supersessionData.find(it => it.ItemId === itemId)?.ExtensionProperties;

        const inventoryBreakdownJSON = getExtensionPropertyValueByKey(
            productExtensionProperties,
            SupersessionKey.InventoryBreakdown,
            CommercePropertyValueEnum.string
        ) as string;

        let warehouse: WarehouseInventoryType | undefined = undefined;
        let isAlternateWarehouse: boolean = false;
        let numOfWarehouse: number | undefined = undefined;
        let primaryWarehouse: WarehouseInventoryType | undefined = undefined;
        let hasWarehouseChanged = false;
        let breakdown: WarehouseInventoryType[] | undefined = undefined;

        if (inventoryBreakdownJSON) {
            breakdown = JSON.parse(inventoryBreakdownJSON) as WarehouseInventoryType[];
            if (breakdown && breakdown.length) {
                // const currWh = cartLine.data?.cartline.WarehouseId;
                // hard-coded first element as its primary warehouse
                primaryWarehouse = breakdown[0];
                const warehouseAttr = cartLine.data?.cartline.AttributeValues?.find(
                    attr => attr.Name === 'WarehouseId'
                ) as AttributeTextValue;
                const warehouseOverrideAttr = cartLine.data?.cartline.AttributeValues?.find(
                    attr => attr.Name === 'IsWarehouseOverride'
                ) as AttributeTextValue;
                if (warehouseOverrideAttr) {
                    hasWarehouseChanged = warehouseOverrideAttr.TextValue === 'true' ? true : false;
                }
                const warehouseId = warehouseAttr?.TextValue || primaryWarehouse.WarehouseId;
                const selectedWarehouse = breakdown.find(w => w.WarehouseId === warehouseId) || breakdown[0];
                // isAlternateWarehouse = selectedWarehouse.IsAllowable === false;
                isAlternateWarehouse = selectedWarehouse.WarehouseId !== primaryWarehouse.WarehouseId;
                numOfWarehouse = breakdown.length;
                warehouse = selectedWarehouse;
            }
        }
        const isCartView: boolean = true;
        const stockLabel = config.stockLabel || resources.defaultInStockLabel;
        const substitutionLabel = config.substitutionLabel || resources.substitutionFallbackLabel;

        const computedState =
            warehouseStateMap.get(cartLine.data?.cartline.LineId || '') ??
            computeWarehouseStateForLine(
                cartLine.data?.cartline!,
                breakdown || undefined,
                supersessionData?.find(p => p.RecordId === cartLine.data?.product?.RecordId)?.ExtensionProperties,
                appConfig,
                stockLabel,
                isAlternateWarehouseSelectionDisabled
            );
        const {
            primaryWarehouseChangedLabel,
            alternateWarehouseChangedLabel,
            primaryWarehouseShippingLabel,
            alternateWarehouseSelectionNotAvailableMessage,
            singleSourcingWarningLabel,
            warehouseAvailabilityLinkLabel,
            multipleWarehouseShippingLabel,
            qtyAvailableOnBackOrderLabel,
            priceLabel,
            substitutionItemHeaderLabel,
            substitutionBackorderItemHeaderLabel,
            substitutionQtyLabel,
            substitutionWarehouseLabel
        } = resources;

        const cartlineClone = React.cloneElement(cartLine.cartline as React.ReactElement, {
            shouldDisplayAlternateWarehouseFeature,
            productExtensionProperties,
            // enforcePrimaryWarehouseAvailability,
            hasWarehouseChanged,
            primaryWarehouse,
            numOfWarehouse,
            isAlternateWarehouseSelectionDisabled,
            isAlternateWarehouse,
            isCartView,
            warehouse,
            selectProductForAvailability: handleLinkClick,
            primaryWarehouseChangedLabel,
            alternateWarehouseChangedLabel,
            primaryWarehouseShippingLabel,
            alternateWarehouseSelectionNotAvailableMessage,
            singleSourcingWarningLabel,
            warehouseAvailabilityLinkLabel,
            stockLabel,
            warehouses: breakdown ?? undefined,
            multipleWarehouseShippingLabel,
            qtyAvailableOnBackOrderLabel,
            computedState,
            substitutionLabel,
            priceLabel,
            substitutionItemHeaderLabel,
            substitutionBackorderItemHeaderLabel,
            substitutionQtyLabel,
            substitutionWarehouseLabel
        });

        return (
            <div className={`msc-cart-lines-item ${isPrintView ? 'gar-cart-line__print-view' : ''}`} key={itemIndex}>
                {cartlineClone}
                <span className='msc-cart-lines-button-container'>
                    {isMultiplePickUp
                        ? null
                        : renderBopisBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                    {renderCartLine(
                        cartLine,
                        resources,
                        config,
                        storeSelector,
                        cartLineComment,
                        advancedNotes,
                        productExtensionProperties,
                        telemetryContent,
                        isMultiplePickUp,
                        emailDeliveryModeCode
                    )}
                </span>
                {renderAdvancedNotes(
                    isPrintView,
                    advancedNotes,
                    notes.filter(n => n.ItemId === itemId)
                )}
            </div>
        );
    });
};

/**
 * Render Cart Lines.
 * @param props - The cart line props.
 * @param cartLines - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param waitingComponent - Waiting component element.
 * @param cartLoadingStatus - Cart loading status string.
 * @param isCartDataResult - Flag to check if there is cart data.
 * @param telemetryContent - The telemetry object.
 * @param isMultiplePickUp - Flag to check if there multiple pickups.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderCartlines = (
    supersessionData: SimpleProduct[],
    handleLinkClick: (lineId: string) => void,
    notes: IAdvancedNoteEntity[],
    isPrintView: boolean,
    props: ICartViewProps & ICartExtensionProps<{}>,
    cartLines: ICartlinesViewProps[] | undefined,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    waitingComponent: React.ReactNode,
    cartLoadingStatus: string,
    isCartDataResult: boolean,
    pagination: React.ReactNode,
    paginatedView: ICartlinesViewProps[] | undefined,
    isAlternateWarehouseSelectionDisabled: boolean,
    shouldDisplayAlternateWarehouseFeature: boolean,
    warehouseStateMap: Map<string, WarehouseState>,
    appConfig: any,
    // enforcePrimaryWarehouseAvailability: boolean,
    // hasWarehouseChanged: boolean | undefined,
    telemetryContent?: ITelemetryContent,
    isMultiplePickUp?: boolean,
    emailDeliveryModeCode?: string
): JSX.Element[] | JSX.Element => {
    const {
        slots: { cartLineComment, orderLevelComment, advancedNotes }
    } = props;
    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }

    // Props.CartlinesWrapper is only required on cart lines and when multiple pickup mode is disabled. This is also required to handle the empty cart styling.
    // Else, empty cart multiple pickup mode is disabled falls under the other cart line wrapper which is creating issue with Empty cart scenario.
    // if (cartLines) {
    if (paginatedView) {
        return props.multiplePickUpEnabled ? (
            <>
                {pagination}
                {renderCartlinesInternal(
                    supersessionData,
                    handleLinkClick,
                    notes,
                    isPrintView,
                    /*cartLines*/ paginatedView,
                    resources,
                    props.config,
                    storeSelector,
                    cartLineComment,
                    advancedNotes,
                    isAlternateWarehouseSelectionDisabled,
                    shouldDisplayAlternateWarehouseFeature,
                    warehouseStateMap,
                    appConfig,
                    // enforcePrimaryWarehouseAvailability,
                    // hasWarehouseChanged,
                    telemetryContent,
                    isMultiplePickUp,
                    emailDeliveryModeCode
                )}
                {pagination}
                {renderOrderLevelComment(isPrintView, orderLevelComment)}
            </>
        ) : (
            <>
                <Node {...props.CartlinesWrapper}>
                    {/*<div>*/}
                    {/*  {renderCartlinesInternal(notes, isPrintView, cartLines, resources, storeSelector, cartLineComment, telemetryContent, isMultiplePickUp, emailDeliveryModeCode)}*/}
                    {/*</div>*/}
                    {pagination}
                    {renderCartlinesInternal(
                        supersessionData,
                        handleLinkClick,
                        notes,
                        isPrintView,
                        /*cartLines*/ paginatedView,
                        resources,
                        props.config,
                        storeSelector,
                        cartLineComment,
                        advancedNotes,
                        isAlternateWarehouseSelectionDisabled,
                        shouldDisplayAlternateWarehouseFeature,
                        warehouseStateMap,
                        appConfig,
                        // enforcePrimaryWarehouseAvailability,
                        // hasWarehouseChanged,
                        telemetryContent,
                        isMultiplePickUp,
                        emailDeliveryModeCode
                    )}
                    {pagination}
                    {renderOrderLevelComment(isPrintView, orderLevelComment)}
                </Node>
            </>
        );
    }

    return isCartDataResult ? (
        <div className='msc-cart__empty-cart'>
            <div className='msc-cart-line'>{props.slots.emptyCart}</div>
        </div>
    ) : (
        <>{waitingComponent}</>
    );
};

/**
 * Render Cart Lines group title.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param count - Count of products.
 * @returns JSX Element.
 */
const renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, resources: ICartResources, count: number) => {
    const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { itemLabel, itemsLabel } = resources;
    const itemCountOne: number = 1;
    const suffix = count > itemCountOne ? itemsLabel : itemLabel;
    const countTitle = `(${count} ${suffix})`;
    return (
        <>
            <Node className='msc-cart-lines-group-wrapper__bopis-heading-title'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wrapper__bopis-heading-title-st'>
                        {cartLine.pickUpInStore?.deliveryOption}
                        {', '}
                        {cartLine.pickUpInStore?.orgUnitName}
                    </p>
                ) : null}
                <p className='msc-cart-lines-group-wrapper__bopis-heading-title-ct'>{countTitle}</p>
            </Node>
        </>
    );
};

/**
 * Render Cart Lines group title.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param count - Count of products.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderCartLinesGroupHeader = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    count: number,
    emailDeliveryModeCode?: string
) => {
    const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { pickUpText, shippingText, emailshippingText } = resources;
    let groupTitle: string;

    groupTitle = isBopisSelected ? pickUpText : shippingText;
    groupTitle = cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailshippingText : groupTitle;

    return (
        <>
            <div className='msc-cart-lines-group-wrapper__bopis-heading'>
                <p className={`msc-cart-lines-group-wrapper__bopis-heading-${groupTitle.toLowerCase().replace(' ', '-')}-icon`} />
                <p className={`msc-cart-lines-group-wrapper__bopis-heading-${groupTitle.toLowerCase().replace(' ', '-')}`}>{groupTitle}</p>
                {renderCartLinesGroupTitle(cartLine, resources, count)}
            </div>
        </>
    );
};

/**
 * Returns count of products.
 * @param cartLine - The cart line view props.
 * @returns Number of products.
 */
const countItems = (cartLine: ICartlinesViewProps[]): number => {
    let countItem = 0;
    const defaultItemCount = 0;
    countItem = cartLine.reduce((count, item) => {
        return count + (item.data?.cartline.Quantity ?? defaultItemCount);
    }, defaultItemCount);
    return countItem;
};

/**
 * Render Cart Lines group.
 * @param props - The cart line view props.
 * @param resources - Module resource strings.
 * @returns JSX Element.
 */
const renderCartLinesGroup = (
    supersessionData: SimpleProduct[],
    handleInventoryLinkClick: (lineId: string) => void,
    advancedNotes: IAdvancedNoteEntity[],
    isPrintView: boolean,
    props: ICartViewProps & ICartExtensionProps<{}>,
    resources: ICartResources,
    pagination: React.ReactNode,
    paginatedView: ICartlinesViewProps[] | undefined,
    isAlternateWarehouseSelectionDisabled: boolean,
    shouldDisplayAlternateWarehouseFeature: boolean,
    warehouseStateMap: Map<string, WarehouseState>,
    appConfig: any
    // enforcePrimaryWarehouseAvailability: boolean
    // hasWarehouseChanged: boolean | undefined
): JSX.Element | undefined => {
    if (props.cartLinesGroup && ArrayExtensions.hasElements(props.cartLinesGroup)) {
        return (
            <div className='msc-cart-lines-group'>
                {props.cartLinesGroup.map(cartlines => {
                    return (
                        <div key={cartlines[0].cartlineId} className='msc-cart-lines-group-wraper'>
                            {renderCartLinesGroupHeader(
                                cartlines[0],
                                resources,
                                countItems(cartlines),
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                            )}
                            {renderCartlines(
                                supersessionData,
                                handleInventoryLinkClick,
                                advancedNotes,
                                isPrintView,
                                props,
                                cartlines,
                                props.resources,
                                props.storeSelector,
                                props.waitingComponent,
                                props.cartLoadingStatus,
                                props.cartDataResult,
                                pagination,
                                paginatedView,
                                isAlternateWarehouseSelectionDisabled,
                                shouldDisplayAlternateWarehouseFeature,
                                warehouseStateMap,
                                appConfig,
                                // enforcePrimaryWarehouseAvailability,
                                // hasWarehouseChanged,
                                props.telemetryContent,
                                props.multiplePickUpEnabled,
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
    return props.cartDataResult ? (
        <div className='msc-cart__empty-cart'>
            <div className='msc-cart-line'>{props.slots.emptyCart}</div>
        </div>
    ) : (
        <>{props.waitingComponent}</>
    );
};

/**
 * Render error block.
 * @param errorData - Error component.
 * @returns JSX Element.
 */
const renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || !ArrayExtensions.hasElements(errorData.errors)) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

/**
 * Render invoice summary line.
 */
const renderInvoiceSummaryLine = (label: string | undefined, amount: string, type: string) => {
    return (
        <p className={`msc-invoice-summary__line-${type}`}>
            <span className='msc-invoice-summary__label'>{label}</span>
            <span className='msc-price msc-invoice-summary__value'>
                <span className='msc-price__actual' itemProp='price'>
                    {amount}
                </span>
            </span>
        </p>
    );
};

/**
 * Custom InvoiceSummary component.
 */
const InvoiceSummary = (props: IInvoiceSummaryProps, invoiceCssLabel: string, totalCssLabel: string) => {
    // const { TotalAmount, CartLines } = props.cart;
    const { CartLines, TotalAmount } = props.cart;
    const invoiceLines = CartLines?.filter(cartLine => cartLine.IsInvoiceLine);
    const total = TotalAmount ? props.context.cultureFormatter.formatCurrency(TotalAmount) : 'N/A';
    const reactNodes: IInvoiceSummaryLines = {
        invoices: invoiceLines?.map((invoiceLine: CartLine) => {
            const amount = invoiceLine.ExtendedPrice ? props.context.cultureFormatter.formatCurrency(invoiceLine.ExtendedPrice) : 'N/A';
            return renderInvoiceSummaryLine(invoiceLine.InvoiceId, amount, invoiceCssLabel);
        }),
        orderTotal: renderInvoiceSummaryLine(props.orderTotalLabel, total, totalCssLabel)
    };
    return reactNodes;
};

/**
 * Gets invoice summary line items.
 * @returns Invoice summary lines.
 */
const getInvoiceSummaryLineItems = (props: ICartViewProps & ICartExtensionProps<{}>): IInvoiceSummaryLines | undefined => {
    const cart = props.cart;
    // cart?.cart?.CartLines?.forEach(cartLine => cartLine.IsInvoiceLine && (cartLine.InvoiceAmount = cartLine.ExtendedPrice));
    return cart?.hasInvoiceLine
        ? InvoiceSummary(
              {
                  orderTotalLabel: props.resources.summaryTotalAmountLabel,
                  invoiceLabel: props.resources.invoiceLabel,
                  cart: cart.cart,
                  context: props.context,
                  typeName: props.typeName,
                  telemetry: props.telemetry,
                  id: props.id
              },
              props.resources.invoiceCssLabel,
              props.resources.totalCssLabel
          )
        : undefined;
};

/**
 * Render invoice summary.
 * @param invoiceSummaryLines - Invoice summary component.
 * @param orderSummaryItems - Order summary component.
 * @param props - The module props.
 * @returns JSX Element.
 */
const renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    orderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...orderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Render order summary.
 * @param orderSummaryLines - Order summary component.
 * @param orderSummaryItems - Order summary items component.
 * @param props - The module props.
 * @returns JSX Element.
 */
const renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    orderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!orderSummaryLines) {
        props.context.telemetry.error('OrderSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...orderSummaryItems}>
            {orderSummaryLines.subtotal}
            {/* {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null} */}
            {orderSummaryLines.shipping}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.tax}
            {orderSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Render error block.
 * @param promotions - The module view props.
 * @returns JSX Element.
 */
const renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                <Node className='msc-cart__promotion_link-section'>{promotions.promotionSeeAllLink}</Node>
                {promotions.promotionDialog}
            </Node>
        </>
    );
};

/**
 * Render order summary checkout.
 * @param props - The module view props.
 * @returns JSX Element.
 */
const RenderOrderSummaryCheckout: React.FC<OrderSummaryCheckoutProps> = ({ props, renderClearCart, shouldDisplaySpringOrderModal }) => {
    const subTotal = props.data.cart.result?.cart.SubtotalAmount || 0;
    const isInvalidBalance = subTotal <= 0;

    const { checkoutAsSignInUserButton, resources } = props;

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isProcessing, setIsProcessing] = React.useState(false);

    const springOrderCouponCode: string = props.context?.app?.config?.springOrderCouponCode ?? '';
    const springOrderModalHeadingText: string = props.context?.app?.config?.springOrderModalHeadingText || '';
    const springOrderModalMessages: string = props.context?.app?.config?.springOrderModalMessages || '';

    const originalOnClickRef = React.useRef<((e?: any) => void) | undefined>(undefined);
    const originalHrefRef = React.useRef<string | undefined>(undefined);
    const lastEventRef = React.useRef<any>(undefined);

    const handleClonedButtonClick = (e: React.MouseEvent) => {
        e.preventDefault?.();
        e.stopPropagation?.();

        lastEventRef.current = e;

        setIsModalOpen(true);
    };

    const buildCheckoutButton = () => {
        if (!React.isValidElement(checkoutAsSignInUserButton)) {
            return checkoutAsSignInUserButton;
        }

        const element = checkoutAsSignInUserButton as React.ReactElement;

        if (isInvalidBalance) {
            return React.cloneElement(element, {
                disabled: true,
                onClick: (e: React.MouseEvent) => {
                    e.preventDefault?.();
                    e.stopPropagation?.();
                }
            });
        }

        if (shouldDisplaySpringOrderModal) {
            return React.cloneElement(element, {
                onClick: (e: React.MouseEvent) => {
                    originalOnClickRef.current = element.props?.onClick;
                    originalHrefRef.current = element.props?.href || element.props?.to;
                    handleClonedButtonClick(e);
                },
                href: undefined,
                to: undefined
            });
        }

        return element;
    };

    const handleConfirm = async () => {
        if (isProcessing) {
            return;
        }
        setIsProcessing(true);

        const promo = springOrderCouponCode?.trim();

        try {
            if (promo && props.data?.cart?.result?.addPromoCode) {
                const result = await props.data.cart.result.addPromoCode({ promoCode: promo });
                if (result.status === 'SUCCESS') {
                    (await props.data.cart).refreshCart({});
                }
            }
        } catch (err) {}

        // const targetHref = originalHrefRef.current;
        // if (targetHref) {
        //     cleanupRefs(false);
        //     window.location.href = targetHref;
        //     return;
        // }

        // if (originalOnClickRef.current) {
        //     try {
        //         originalOnClickRef.current(lastEventRef.current);
        //     } catch (err) {
        //     } finally {
        //         cleanupRefs();
        //     }
        // } else {
        //     cleanupRefs();
        // }

        cleanupRefs();
    };

    const handleCancel = () => {
        if (isProcessing) {
            return;
        }

        const targetHref = originalHrefRef.current;
        if (targetHref) {
            cleanupRefs(false);
            window.location.href = targetHref;
            return;
        }
        if (originalOnClickRef.current) {
            try {
                originalOnClickRef.current(lastEventRef.current);
            } catch (err) {}
        }
        cleanupRefs();
    };

    const handleClose = () => {
        if (isProcessing) {
            return;
        }

        cleanupRefs(true);
    };

    const cleanupRefs = (closeModal = true) => {
        originalOnClickRef.current = undefined;
        originalHrefRef.current = undefined;
        lastEventRef.current = undefined;
        setIsProcessing(false);
        if (closeModal) {
            setIsModalOpen(false);
        }
    };

    return (
        <>
            <Node className='msc-order-summary__checkout'>
                {props.cart?.hasInvoiceLine
                    ? null
                    : React.cloneElement(props.promoCode as React.ReactElement, { couponMapping: props.config?.couponCodeMap })}
                <Node className='msc-order-summary__checkout__action'>
                    {buildCheckoutButton()}
                    {props.checkoutAsGuestButton}
                </Node>
                {props.createTemplateFromCartButton}
                {renderClearCart}
            </Node>

            <Modal isOpen={isModalOpen} toggle={handleClose} className='spring-order-modal'>
                <ModalHeader toggle={handleClose} className='spring-order-modal__header'>
                    {springOrderModalHeadingText ? (
                        <Msdyn365.RichTextComponent text={springOrderModalHeadingText} />
                    ) : (
                        resources.springOrderModalHeading
                    )}
                </ModalHeader>
                <ModalBody className='spring-order-modal__body'>
                    <p className='spring-order-modal__body__messages'>
                        <Msdyn365.RichTextComponent text={springOrderModalMessages} />
                    </p>
                    <div className='spring-order-modal__body__actions'>
                        <Button onClick={handleConfirm} disabled={isProcessing}>
                            {resources.yesButtonLabel}
                        </Button>
                        <Button onClick={handleCancel} disabled={isProcessing}>
                            {resources.noButtonLabel}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
};

const renderReminderMessage = (props: ICartViewProps & ICartExtensionProps<{}>): JSX.Element => {
    const {
        config: { couponReminderMessage, couponReminderHeading }
    } = props;

    if (!couponReminderMessage) {
        return <></>;
    }
    return (
        <Node className='msc-order-summary__reminder'>
            {couponReminderHeading && couponReminderHeading.trim() && (
                <div className='msc-order-summary__reminder--heading'>{couponReminderHeading}</div>
            )}
            <Msdyn365.RichTextComponent text={couponReminderMessage} className='msc-order-summary__reminder--message' />
        </Node>
        // <Node className='msc-order-summary__reminder'>
        //     <span className='msc-order-summary__reminder_text'>
        //         <strong>{props.config.couponReminderHeading}: </strong>
        //         {props.config.couponReminderMessage}
        //     </span>
        // </Node>
    );
};

/**
 * Renders the clear cart button on the page. Clicking the button triggers the modal to confirm the action.
 *
 * @param {(e: React.SetStateAction<boolean>) => void} setClearCartModalActive
 * @param {ICartResources} resources
 * @return {*}  {React.ReactNode}
 */
const _renderClearCart = (
    setClearCartModalActive: (e: React.SetStateAction<boolean>) => void,
    resources: ICartResources
): React.ReactNode => (
    <div className={`msc-cart__clear-cart-container`}>
        <button
            className='msc-cart__clear-cart-container--btn'
            title={resources.clearCartButtonText}
            onClick={() => setClearCartModalActive(true)}
        >
            {resources.clearCartButtonText}
        </button>
    </div>
);

/**
 * Modal which is responsible for verifying the clear cart intention. Can either be dismissed or confirmed. If confirmed, will clear the cart.
 *
 * @param {(e: React.MouseEvent<HTMLButtonElement>) => void} handleClearCart
 * @param {(e: React.SetStateAction<boolean>) => void} setClearCartModalActive
 * @param {() => void} toggle
 * @param {boolean} clearCartModalActive
 * @param {ICartResources} resources
 * @return {*}
 */
const _renderClearCartModal = (
    handleClearCart: (e: React.MouseEvent<HTMLButtonElement>) => void,
    setClearCartModalActive: (e: React.SetStateAction<boolean>) => void,
    toggle: () => void,
    clearCartModalActive: boolean,
    resources: ICartResources
) => {
    const clearCartClassName = 'clearcart';

    /**
     * Renders the button area
     */
    const _renderButtonArea = (): React.ReactNode => (
        <div className={`${clearCartClassName}__button`}>
            <Button onClick={handleClearCart} className={`${clearCartClassName}__button--save`}>
                {resources.clearCartButtonText}
            </Button>
            <Button onClick={() => setClearCartModalActive(false)} className={`${clearCartClassName}__button--cancel`}>
                {resources.clearCancelButtonText}
            </Button>
        </div>
    );

    return (
        <Modal className={`${clearCartClassName}`} isOpen={clearCartModalActive} toggle={toggle}>
            <ModalHeader className={`${clearCartClassName}__modal-header`}>{resources.clearCartHeader}</ModalHeader>
            <ModalBody className={`${clearCartClassName}__modal-body`}>{resources.clearConfirmBody}</ModalBody>
            <ModalFooter>{_renderButtonArea()}</ModalFooter>
        </Modal>
    );
};

/**
 * Render order summary checkout button.
 * @param props - The module view props.
 * @returns JSX Element.
 */
const renderOrderSummaryCheckoutButton = (props: ICartViewProps): JSX.Element | undefined => {
    return (
        <Node className='msc-order-summary__checkout-button'>
            {props.checkoutAsSignInUserButton}
            {props.checkoutAsGuestButton}
        </Node>
    );
};

const _renderPrintView = (
    isPrintView: boolean,
    handlePrintViewChange: (e: React.MouseEventHandler<HTMLInputElement>) => void,
    printViewLabel: string
): React.ReactNode => (
    <div className='input-group print-view-toggle'>
        <label className='input-group-prepend print-view-toggle__label'>
            <span className={'print-view-toggle__heading'}>{printViewLabel}</span>
            {/* @ts-ignore */}
            <input onClick={handlePrintViewChange} type='checkbox' className='print-view-toggle__checkbox' />
            <div className={`print-view-toggle__toggle print-view-toggle__toggle--${isPrintView ? 'checked' : 'unchecked'}`} />
        </label>
    </div>
);

/**
 * Renders the advanced-notes that display all of the associated notes for each cart line item at the bottom of the item in the cart.
 */

const renderAdvancedNotes = (isPrintView: boolean, advancedNotes: React.ReactNode, notes: IAdvancedNoteEntity[]): React.ReactNode => {
    if (!advancedNotes) {
        return null;
    }

    return (
        <div className={`${isPrintView ? 'advanced-notes-container--print-view' : ''}`}>
            {/* @ts-ignore */}
            {React.cloneElement(advancedNotes[0] as React.ReactElement, { notes })}
        </div>
    );
};

/**
 * Renders the order-level-comment that displays a comment at the bottom of the cart.
 */

const renderOrderLevelComment = (isPrintView: boolean, orderLevelComment: React.ReactNode | undefined): React.ReactNode => {
    if (!orderLevelComment) {
        return null;
    }

    return <div className={`order-comment-container ${isPrintView ? 'order-comment-container__print-view' : ''}`}>{orderLevelComment}</div>;
};

const RenderCreditBalanceMessage: React.FC<CreditBalanceMsgProps> = ({ props }) => {
    const {
        data: { cart }
    } = props;
    const cartState = cart?.result?.cart;

    if (!cartState) {
        return null;
    }
    const hasCreditApplied = cartState.CartLines?.some(line => line.InvoiceId?.startsWith('CN') || line.InvoiceId?.startsWith('FTC'));

    const subtotal = cartState.SubtotalAmount ?? 0;

    const isBlocked = subtotal <= 0 && hasCreditApplied;

    if (!isBlocked) {
        return null;
    }

    const message = props.context.app.config.belowMinimumMessage || props.resources.belowMinimumFallbackMessage;

    return <div className='msc-cart__checkout-error'>{message}</div>;
};

const RenderSupersessionReplacementMessage: React.FC<SupersessionMsgProps> = ({ props }) => {
    const {
        config,
        resources,
        data: { cart }
    } = props;

    const cartState = cart?.result?.cart;

    const hasSupersessionPreviewAllocations = React.useMemo(() => {
        return (cartState?.CartLines ?? []).some(cl => hasSupersessionPreview(cl));
    }, [cartState?.CartLines]);

    const shouldShow =
        !!cartState && !cart.result?.hasInvoiceLine && (cartState.CartLines?.length ?? 0) > 0 && hasSupersessionPreviewAllocations;

    const [dismissed, setDismissed] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);

    React.useEffect(() => {
        if (!shouldShow) {
            setDismissed(false);
            setIsOpen(false);
            return;
        }

        if (!dismissed) {
            setIsOpen(true);
        }
    }, [shouldShow, dismissed]);

    const onDismiss = () => {
        setDismissed(true);
        setIsOpen(false);
    };

    if (!shouldShow) {
        return null;
    }

    const message = config.supersessionReplacementLabel ?? resources.supersessionReplacementFallbackLabel;

    return (
        <Alert className='ms-cart__supersession-message' isOpen={isOpen} aria-label={message}>
            <div className='ms-cart__supersession-message__header' aria-hidden='true'>
                <span className='supersession-info-icon' />
                <span>{message}</span>
                <button className='ms-cart__supersession-message-toggle' onClick={onDismiss} aria-label='Dismiss'></button>
            </div>
        </Alert>
    );
};

/**
 * Render cart view.
 * @param props - The module view props.
 * @returns JSX Element.
 */
const CartView: React.FC<ICartViewProps & ICartExtensionProps<{}>> = (props: ICartViewProps & ICartExtensionProps<{}>) => {
    const {
        config: { shouldRenderReminder, couponReminderMessage }
    } = props;
    const handlePrintViewChange = () => setIsPrintView(!isPrintView);
    const [isPrintView, setIsPrintView] = React.useState<boolean>(false);
    const [advancedNotes, setAdvancedNotes] = React.useState<IAdvancedNoteEntity[] | undefined>(undefined);
    const [supersessionData, setSupersessionData] = React.useState<SimpleProduct[]>([]);
    const [selectedLineForInventoryLookup, setSelectedLineForInventoryLookup] = React.useState<string | undefined>(undefined);
    const printViewToggle = _renderPrintView(isPrintView, handlePrintViewChange, 'Print View');
    const totalItemCountOne: number = 1;
    const defaultPageLength: number = 5;
    const updatedTitle = (
        <h1 className='msc-cart__heading'>
            {props.resources.cartTitle}
            <span className='msc-cart__heading-count-items'>
                {` (${Number(props.cart?.totalItemsInCart)} ${props.cart?.totalItemsInCart !== totalItemCountOne ? 'items' : 'item'})`}
            </span>
        </h1>
    );
    const getAdvancedNotes = async (): Promise<void> => {
        const itemIds = props.cart?.cart.CartLines?.map(cl => cl.ItemId!) || [];
        if (!itemIds.length) {
            return;
        }
        getAdvancedNotesAsync({ callerContext: props.context.actionContext }, itemIds).then(notes => {
            setAdvancedNotes(notes || []);
        });
    };

    const pageLength = props.config.pageLength || defaultPageLength;

    const [paginatedView, setPaginatedView] = React.useState<ICartlinesViewProps[] | undefined>(
        props.cartlines && props.cartlines.length >= pageLength ? props.cartlines.slice(0, pageLength) : props.cartlines
    );
    const [paginationPage, setPaginationPage] = React.useState<number>(0);
    const [clearCartModalActive, setClearCartModalActive] = React.useState<boolean>(false);

    const handleClearCart = () => {
        const cart = props.cart;
        const cartlines = cart?.cart.CartLines;

        if (cart && cartlines) {
            let ids = { cartLineIds: Array() };
            cartlines.map(c => (c.LineId ? ids.cartLineIds.push(c.LineId) : null));
            cart.removeCartLines(ids);
            setClearCartModalActive(false);
            cart.refreshCart({});
        }
    };

    /**
     * Utility class to easily go up one page
     */
    const nextPage = () => {
        changePage(paginationPage + 1);
    };

    /**
     * Utility class to easily go down one page
     */
    const prevPage = () => {
        changePage(paginationPage - 1);
    };

    /**
     * Update the state of @const paginationPage and @const paginatedView to the given page, folliowing some logic
     * to prevent the page from going into the negatives or beyond the maximum page length.
     *
     * @param {number} page
     * @return {*}
     */
    const changePage = (page: number) => {
        if (page < 0) {
            return;
        }

        if (props.cartlines && page >= props.cartlines.length / pageLength) {
            page = props.cartlines.length / pageLength - 1;
        }

        let skip = page * pageLength;
        if (props.cartlines && props.cartlines.length >= skip) {
            setPaginationPage(page);
            let slice = props.cartlines?.slice(skip);
            setPaginatedView(slice.length >= pageLength ? slice.slice(0, pageLength) : slice);
        }
    };
    const [shouldShowQtyMessage, setShouldShowQtyMessage] = React.useState(false);

    // const [hasWarehouseChanged, setHasWarehouseChanged] = React.useState<boolean | undefined>(undefined);

    // Tracks if alternate warehouse selection should be disabled due to applied coupon codes.
    const [isAlternateWarehouseSelectionDisabled, setIsAlternateWarehouseSelectionDisabled] = React.useState(false);

    // Determines whether the alternate warehouse feature should be displayed for the user.
    const [shouldDisplayAlternateWarehouseFeature, setShouldDisplayAlternateWarehouseFeature] = React.useState(false);

    // Indicates if the system should enforce primary warehouse availability before allowing alternate warehouse selection.
    // const [enforcePrimaryWarehouseAvailability, setEnforcePrimaryWarehouseAvailability] = React.useState(true);

    // List of coupon codes that will allow warehouse selection on the cart page.
    const allowableCouponCodesForWarehouseSelection: string[] = props.context.app.config.allowableCouponCodesForWarehouseSelection || [];

    // List of customer account numbers that are authorized to use the alternate warehouse feature.
    const authorizedWarehouseAccounts: string[] = props.context.app.config.authorizedWarehouseAccounts || [];

    // List of dealer types that are authorized to use the alternate warehouse feature.
    const allowedDealerTypes: string = props.context.app.config.allowedDealerTypes || '';

    // List of comma- or semicolon-separated string (e.g., '01,16,99') customer groups that are authorized to use the alternate warehouse feature.
    const designatedCustomerGroups: string = props.context.app.config.designatedCustomerGroups || '';

    // convert customer groups into an array of strings
    const designatedCustomerGroupsArr = convertStringToArray(designatedCustomerGroups);
    const allowedDealerTypesArr = convertStringToArray(allowedDealerTypes);

    // Configuration flag to enforce primary warehouse availability before selecting an alternate warehouse.
    // const isPrimaryWarehouseEnforced: boolean = props.context.app.config.enforcePrimaryWarehouseAvailability;

    // Configuration flag to enable or disable the alternate warehouse feature.
    const isAlternateWarehouseFeatureEnabled: boolean = props.context.app.config.enableAlternateWarehouseFeature;

    // Spring order feature
    const [shouldDisplaySpringOrderModal, setShouldDisplaySpringOrderModal] = React.useState<boolean>(false);
    const subTotal = props.data.cart.result?.cart.SubtotalAmount || 0;
    const isSpringFeaturedEnabled: boolean = props.context.app.config.shouldDisplaySpringOrderFeature || false;
    const springOrderCouponCode: string = props.context.app.config.springOrderCouponCode || '';
    const springOrderThresholdAmount: number =
        props.context.app.config.springOrderThresholdAmount !== undefined
            ? parseFloat(Number(props.context.app.config.springOrderThresholdAmount).toFixed(2))
            : Number.MAX_VALUE;

    const springOrderDesignatedCustomerGroups: string = props.context.app.config.springOrderDesignatedCustomerGroups || '';
    const springOrderDesignatedCustomerGroupsArr = convertStringToArray(springOrderDesignatedCustomerGroups);

    const springOrderDesignatedFreightTermCodes: string = props.context.app.config.springOrderDesignatedFreightTermCodes || '';
    const springOrderDesignatedFreightTermCodesArr = convertStringToArray(springOrderDesignatedFreightTermCodes);

    const couponCodes = props.cart?.cart.Coupons?.map(coupon => coupon.Code) || [];

    const couponCodeAlreadyExists = couponCodes.some(code => code === springOrderCouponCode);

    const getSupersessionData = async () => {
        const data = await getSupersessionV2Async(
            { callerContext: props.context.actionContext },
            props.cart?.cart.CartLines?.map(cl => cl.ItemId!) || [],
            [],
            true,
            props.cart?.cart.Id || ''
        );
        setSupersessionData(data);
        return data;
    };

    React.useEffect(() => {
        const checkSpringOrder = async () => {
            setShouldDisplaySpringOrderModal(false);

            if (!isSpringFeaturedEnabled) {
                return;
            }

            if (isNaN(springOrderThresholdAmount) || subTotal < springOrderThresholdAmount) {
                return;
            }

            if (couponCodeAlreadyExists) {
                return;
            }

            let products = supersessionData;

            if (products.length === 0) {
                products = await getSupersessionData();
            }

            const firstExt = products[0]?.ExtensionProperties;

            let additionalAltWarehouseDetails: AdditionalAltWarehouseDetails | undefined;
            try {
                const detailsString = getAdditionalAltWarehouseDetails(firstExt);
                additionalAltWarehouseDetails = detailsString ? (JSON.parse(detailsString) as AdditionalAltWarehouseDetails) : undefined;
            } catch {
                return;
            }

            if (!additionalAltWarehouseDetails) {
                return;
            }

            const customerGroup = (additionalAltWarehouseDetails.CustomerGroup ?? '').toString().trim();
            const freightTermCode = additionalAltWarehouseDetails.FreightTermCodeId.toString().trim();

            if (
                springOrderDesignatedCustomerGroupsArr.includes(customerGroup) &&
                springOrderDesignatedFreightTermCodesArr.includes(freightTermCode)
            ) {
                setShouldDisplaySpringOrderModal(true);
            }
        };

        checkSpringOrder();
    }, [
        isSpringFeaturedEnabled,
        springOrderThresholdAmount,
        subTotal,
        couponCodeAlreadyExists,
        supersessionData,
        springOrderDesignatedCustomerGroupsArr,
        springOrderDesignatedFreightTermCodesArr
    ]);

    const getWarehouseData = (cartline: CartLine, supersessionData: SimpleProduct[]): WarehouseInventoryType[] | null => {
        const supersessionProductRecord = supersessionData.find(sd => sd.RecordId === cartline.ProductId);
        if (supersessionProductRecord && supersessionProductRecord.ExtensionProperties) {
            const warehouseJsonData = getExtensionPropertyValueByKey(
                supersessionProductRecord.ExtensionProperties,
                SupersessionKey.InventoryBreakdown,
                CommercePropertyValueEnum.string
            ) as string;

            if (warehouseJsonData) {
                try {
                    return JSON.parse(warehouseJsonData) as WarehouseInventoryType[];
                } catch (e) {
                    console.error('Error parsing warehouse data for cartline', cartline, e);
                }
            }
        }
        return null;
    };

    const processCartLines = async (conditionFn: (warehouseData: WarehouseInventoryType[], cartline: CartLine) => boolean | undefined) => {
        const {
            data: {
                cart: { result }
            }
        } = props;

        if (!result) return;
        const { cart } = result;
        if (!cart.CartLines || cart.CartLines.length === 0) return;

        let changesMade = false;
        let supersessionDataToUse = supersessionData;

        if (!supersessionDataToUse || supersessionDataToUse.length === 0) {
            supersessionDataToUse = await getSupersessionData();
        }

        const updatedCartLines = cart.CartLines.map(cartline => {
            const warehouseData = getWarehouseData(cartline, supersessionDataToUse);

            if (warehouseData && warehouseData.length > 0 && conditionFn(warehouseData, cartline)) {
                const primaryWarehouseId = warehouseData[0].WarehouseId;
                changesMade = true;

                const isWarehouseOverrideByQty = getExtensionPropertyValueByKey(
                    cartline?.ExtensionProperties,
                    'IsWarehouseOverrideByQuantity',
                    CommercePropertyValueEnum.boolean
                ) as boolean;

                let updatedExtensionProperties = upsertBoolExtensionProperty(
                    cartline.ExtensionProperties,
                    'IsWarehouseOverrideByQuantity',
                    false
                );

                if (conditionFn === conditionForQuantityAvailability && !isWarehouseOverrideByQty) {
                    updatedExtensionProperties = upsertBoolExtensionProperty(updatedExtensionProperties, 'HasWarehouseChanged', true);
                }

                let updatedAttributeValues = upsertAttributeTextValue(cartline.AttributeValues, 'IsWarehouseOverride', 'false');

                updatedAttributeValues = upsertAttributeTextValue(updatedAttributeValues, 'WarehouseId', '');

                const newCartLine = {
                    ...cartline,
                    WarehouseId: primaryWarehouseId,
                    AttributeValues: updatedAttributeValues,
                    ExtensionProperties: updatedExtensionProperties
                };

                return newCartLine;
            }
            return cartline;
        });

        if (!changesMade) {
            return;
        }

        try {
            await result.updateCartLines({
                updatedCartLinesObject: updatedCartLines
            });
        } catch (e) {
            console.error('Error updating cart lines during revert', e);
        }
    };

    const conditionForWarehouseOverride = (warehouseData: WarehouseInventoryType[], cartline: CartLine) => {
        const warehouseAttr: AttributeTextValue | undefined = cartline.AttributeValues?.find(attr => attr.Name === 'WarehouseId');
        return warehouseAttr && warehouseAttr.TextValue !== '';
    };

    const conditionForQuantityAvailability = (warehouseData: WarehouseInventoryType[], cartline: CartLine) => {
        const warehouseAttr: AttributeTextValue | undefined = cartline.AttributeValues?.find(attr => attr.Name === 'WarehouseId');
        if (!warehouseAttr) return false;
        const primaryWarehouseId = warehouseData[0]?.WarehouseId;
        if (warehouseAttr.TextValue === primaryWarehouseId) {
            return false;
        }
        const selectedWarehouse = warehouseData.find(wh => wh.WarehouseId === warehouseAttr.TextValue);
        if (!selectedWarehouse) return false;
        const availableQty = selectedWarehouse.Available || 0;
        return cartline.Quantity !== undefined && cartline.Quantity > availableQty;
    };

    const revertCartlinesWarehouse = React.useCallback(async () => {
        await processCartLines(conditionForWarehouseOverride);
    }, [allowableCouponCodesForWarehouseSelection, props.cart?.cart.Coupons?.length]);

    const revertCartLineToPrimaryWarehouse = React.useCallback(async () => {
        await processCartLines(conditionForQuantityAvailability);
    }, [props.cart?.cart.CartLines, supersessionData]);

    React.useEffect(() => {
        const couponCodes = props.cart?.cart.Coupons?.map(coupon => coupon.Code) || [];

        const hasInvalidCoupon = couponCodes.some(code => code && !allowableCouponCodesForWarehouseSelection.includes(code));

        if (hasInvalidCoupon) {
            revertCartlinesWarehouse();
            setIsAlternateWarehouseSelectionDisabled(true);
        } else {
            setIsAlternateWarehouseSelectionDisabled(false);
        }
    }, [allowableCouponCodesForWarehouseSelection, props.cart?.cart.Coupons?.length]);

    React.useEffect(() => {
        const checkAlternateWarehouseFeature = async () => {
            let supersessionProducts = supersessionData;
            if (!supersessionProducts || supersessionProducts.length === 0) {
                supersessionProducts = await getSupersessionData();
            }
            const shouldDisplayAlternateWarehouse = await checkAlternateWarehouseFeatureAccess({
                extensionProperties: supersessionProducts[0]?.ExtensionProperties,
                allowedDealerTypes: allowedDealerTypesArr,
                designatedCustomerGroupsArr,
                authorizedWarehouseAccounts,
                customerAccNum: props.context.request.user.customerAccountNumber,
                isAlternateWarehouseFeatureEnabled,
                isSobo: props.context.request.user.isOBORequest || false,
                context: props.context.actionContext
            });

            setShouldDisplayAlternateWarehouseFeature(shouldDisplayAlternateWarehouse);
        };

        checkAlternateWarehouseFeature();
    }, []);

    React.useEffect(() => {
        revertCartLineToPrimaryWarehouse();
    }, [revertCartLineToPrimaryWarehouse]);

    const warehouseStateMap = React.useMemo(() => {
        const map = new Map<string, WarehouseState>();
        const cartLines = props.cart?.cart.CartLines || [];
        const productsExt = supersessionData || [];

        cartLines.forEach(cl => {
            const warehouseData = getWarehouseData(cl, productsExt) || undefined;
            const productExt = productsExt.find(p => p.RecordId === cl.ProductId)?.ExtensionProperties;
            const ws = computeWarehouseStateForLine(
                cl,
                warehouseData,
                productExt,
                props.context.app.config,
                props.config.stockLabel || props.resources.defaultInStockLabel,
                isAlternateWarehouseSelectionDisabled
            );
            if (cl.LineId) {
                map.set(cl.LineId, ws);
            }
        });
        return map;
    }, [
        props.cart?.cart.CartLines,
        supersessionData,
        props.context.app.config,
        isAlternateWarehouseSelectionDisabled,
        props.config.stockLabel,
        props.resources.defaultInStockLabel,
        props.cart?.cart.Coupons?.length
    ]);

    const autoSourcedLinesRef = React.useRef<Set<string>>(new Set());

    React.useEffect(() => {
        const {
            data: {
                cart: { result }
            }
        } = props;

        if (!result) {
            return;
        }

        const cartLines = props.cart?.cart.CartLines || [];
        const updates: CartLine[] = [];

        cartLines.forEach(cl => {
            const lineId = cl.LineId;
            if (!lineId) {
                return;
            }
            const state = warehouseStateMap.get(lineId);
            if (!state) {
                return;
            }

            const couponCodes = props.cart?.cart.Coupons?.map(coupon => coupon.Code) || [];

            const hasInvalidCoupon = couponCodes.some(code => code && !allowableCouponCodesForWarehouseSelection.includes(code));

            if (
                state.shouldUpdateWarehouse &&
                !state.isWarehouseOverride &&
                !autoSourcedLinesRef.current.has(lineId) &&
                !hasInvalidCoupon
            ) {
                const targetWarehouseId = state.shipmentInfo?.warehouseId;
                if (
                    targetWarehouseId &&
                    targetWarehouseId !== (cl.AttributeValues?.find(a => a.Name === 'WarehouseId') as AttributeTextValue)?.TextValue
                ) {
                    updates.push(buildUpdatedCartLine(cl, targetWarehouseId));
                    autoSourcedLinesRef.current.add(lineId);
                }
            }
        });

        if (updates.length === 0) {
            return;
        }

        (async () => {
            try {
                await result.updateCartLines({
                    updatedCartLinesObject: updates
                });
                await result.refreshCart({});
            } catch (err) {
                console.error('Batch auto-source update failed:', err);
            }
            updates.forEach(u => u.LineId && autoSourcedLinesRef.current.delete(u.LineId));
        })();
    }, [warehouseStateMap, props.cart?.cart.CartLines, props.cart?.cart.Coupons?.length]);

    const handleWarehouseChange = async (warehouseId: string | null) => {
        if (!warehouseId) {
            return;
        }
        const {
            data: {
                cart: { result }
            }
        } = props;

        if (result) {
            const { cart } = result;
            const cartLine = cart.CartLines?.find(cl => cl.LineId === selectedLineForInventoryLookup);
            let supersessionDataToUse = supersessionData;

            if (!supersessionDataToUse || supersessionDataToUse.length === 0) {
                supersessionDataToUse = await getSupersessionData();
            }
            if (cartLine) {
                const warehouseData = getWarehouseData(cartLine, supersessionDataToUse);
                const primaryWarehouseId = warehouseData?.[0]?.WarehouseId;

                const updatedCartLines: CartLine[] = [...(cart.CartLines || [])];
                const idx = updatedCartLines.findIndex(cl => cl.LineId === cartLine.LineId);

                if (idx > -1) {
                    const primaryId = String(primaryWarehouseId);
                    const selectedId = String(warehouseId);

                    const warehouseAttr = updatedCartLines[idx]?.AttributeValues?.find(
                        attr => attr.Name === 'WarehouseId'
                    ) as AttributeTextValue;

                    if (warehouseAttr && selectedId === warehouseAttr?.TextValue) {
                        setHasClickedAlternateWarehouse(false);
                        setSelectedLineForInventoryLookup(undefined);
                        return;
                    }

                    const extensionProp = getExtensionPropertyValueByKey(
                        updatedCartLines[idx]?.ExtensionProperties,
                        'IsWarehouseOverrideByQuantity',
                        CommercePropertyValueEnum.boolean
                    ) as boolean;

                    let updatedExtensionProperties = updatedCartLines[idx]?.ExtensionProperties ?? [];

                    updatedExtensionProperties = upsertBoolExtensionProperty(updatedExtensionProperties, 'HasWarehouseChanged', false);
                    updatedExtensionProperties = upsertBoolExtensionProperty(
                        updatedExtensionProperties,
                        'IsWarehouseOverrideByQuantity',
                        false
                    );

                    const shouldResetAutoSourceMsg = selectedId !== primaryId && extensionProp;
                    if (shouldResetAutoSourceMsg) {
                        updatedExtensionProperties = upsertBoolExtensionProperty(
                            updatedExtensionProperties,
                            'shouldDisplayAutoSourcedMessage',
                            false
                        );
                    }

                    const warehouseToSelect = warehouseData?.find(wh => wh.WarehouseId === selectedId);

                    const shouldUpdate =
                        shouldEnableSecondaryWarehouses(supersessionDataToUse[0].ExtensionProperties) &&
                        (updatedCartLines[idx]?.Quantity || 0) > (warehouseToSelect?.Available ?? 0) &&
                        warehouseToSelect?.WarehouseId === warehouseData?.[0].WarehouseId;

                    if (selectedId === primaryId && (extensionProp || shouldUpdate)) {
                        updatedExtensionProperties = upsertBoolExtensionProperty(
                            updatedExtensionProperties,
                            'shouldDisplayAutoSourcedMessage',
                            true
                        );
                    }

                    let updatedAttributeValues = updatedCartLines[idx]?.AttributeValues as AttributeTextValue[] | undefined;

                    updatedAttributeValues = upsertAttributeTextValue(
                        updatedAttributeValues,
                        'IsWarehouseOverride',
                        primaryId === selectedId ? 'false' : 'true'
                    );

                    updatedAttributeValues = upsertAttributeTextValue(
                        updatedAttributeValues,
                        'WarehouseId',
                        primaryId === selectedId ? '' : selectedId
                    );

                    updatedCartLines[idx] = {
                        ...updatedCartLines[idx],
                        WarehouseId: warehouseId,
                        AttributeValues: updatedAttributeValues,
                        ExtensionProperties: updatedExtensionProperties
                    };
                }

                try {
                    await result.updateCartLines({
                        updatedCartLinesObject: updatedCartLines
                    });
                    setHasClickedAlternateWarehouse(false);
                    setSelectedLineForInventoryLookup(undefined);
                } catch (e) {
                    console.error('Cart update error:', e);
                }
            }
        }
    };

    const [hasClickedAlternateWarehouse, setHasClickedAlternateWarehouse] = React.useState(false);
    const [selectedWarehouseId, setSelectedWarehouseId] = React.useState<string | null>(null);
    const [isAlternateWarehouse, setIsAlternateWarehouse] = React.useState(false);

    const shouldDisplayFreightTermModal = hasClickedAlternateWarehouse && isAlternateWarehouse;

    const renderAvailabilityModal = () => {
        const cartline = props.data.cart.result?.cart.CartLines?.find(cl => cl.LineId === selectedLineForInventoryLookup);
        const warehouseAttr = cartline?.AttributeValues?.find(att => att.Name === 'WarehouseId') as AttributeTextValue;
        const currentWarehouse = warehouseAttr?.TextValue ?? null;

        const productId = cartline?.ProductId;
        const supersessionProductRecord = supersessionData.find(sd => sd.RecordId === productId);
        return (
            <Modal
                isOpen={!!selectedLineForInventoryLookup}
                toggle={() => {
                    setSelectedLineForInventoryLookup(undefined);
                    setHasClickedAlternateWarehouse(false);
                }}
            >
                <ModalHeader toggle={() => setSelectedLineForInventoryLookup(undefined)}>
                    {!shouldDisplayFreightTermModal && 'Warehouse Availability'}
                </ModalHeader>
                <ModalBody className='alternate-warehouse__modal__body'>
                    {shouldDisplayFreightTermModal ? (
                        <div>
                            <p className='warehouse-terms'>
                                <Msdyn365.RichTextComponent text={props.context.app.config.alternateWarehouseFreightMessage} />
                            </p>
                            <div className='warehouse-terms-buttons'>
                                <Button className='accept-button' onClick={() => handleWarehouseChange(selectedWarehouseId)}>
                                    Accept
                                </Button>
                                <Button
                                    className='cancel-button'
                                    onClick={() => {
                                        setSelectedLineForInventoryLookup(undefined);
                                        setHasClickedAlternateWarehouse(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className='item-info'>
                                <strong>Item:</strong> {cartline?.ItemId!}
                            </p>
                            {shouldShowQtyMessage && (
                                <p className='item-info'>
                                    <Msdyn365.RichTextComponent text={props.context.app.config.qtyNotAvailableFromWarehouseLabel} />
                                </p>
                            )}
                            <InventoryBreakdownComponent
                                extensionProperties={supersessionProductRecord?.ExtensionProperties}
                                itemId={supersessionProductRecord?.ItemId!}
                                canBackorder={true}
                                productQuantity={cartline?.Quantity}
                                designatedDealerTypes={props.context.app.config.designatedDealerTypes}
                                productCodeWhitelist={props.context.app.config.productCodeWhitelist}
                                canChangeWarehouse={true}
                                onWarehouseChange={handleWarehouseChange}
                                allowableText={props.context.app.config.allowableFreightWarehouseLabel}
                                alternateText={props.context.app.config.preferredWarehouseLabel}
                                setShouldShowQtyMessage={setShouldShowQtyMessage}
                                warehouseMappings={props.context.app.config.alternateWarehouseMappings}
                                setIsAlternateWarehouse={setIsAlternateWarehouse}
                                setSelectedWarehouseId={setSelectedWarehouseId}
                                setHasClickedAlternateWarehouse={setHasClickedAlternateWarehouse}
                                currentWarehouse={currentWarehouse}
                            />
                            <p className='note'>
                                <Msdyn365.RichTextComponent text={props.context.app.config.dealerFreightPolicyInfo} />
                            </p>
                        </>
                    )}
                </ModalBody>
            </Modal>
        );
    };

    React.useEffect(() => {
        if (!advancedNotes) void getAdvancedNotes();
        if (!supersessionData.length) void getSupersessionData();
    }, [props.data]);

    React.useEffect(() => {
        changePage(paginationPage);
    }, props.cartlines);

    const toggle = () => setClearCartModalActive(!clearCartModalActive);
    const renderClearCart = _renderClearCart(setClearCartModalActive, props.resources);

    let pagination = (
        <LinePagination
            nextPage={nextPage}
            prevPage={prevPage}
            changePage={changePage}
            currentPage={paginationPage}
            numPages={props.cartlines ? Math.ceil(props.cartlines.length / pageLength) : 0}
            prev={props.resources.paginationPrev}
            next={props.resources.paginationNext}
        />
    );

    const handleInventoryLinkClick = (lineId: string) => {
        setSelectedLineForInventoryLookup(lineId);
    };

    const invoiceSummaryLineitems = getInvoiceSummaryLineItems(props);

    return (
        <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
            <RenderSupersessionReplacementMessage props={props} />
            {updatedTitle}
            <div className='ms-cart__estimated-total'>{props.orderSummaryLineitems?.orderTotal}</div>
            {renderOrderSummaryCheckoutButton(props)}
            {!props.cart?.hasInvoiceLine && props.promotionOptions && renderPromotions(props.promotionOptions)}
            {props.multiplePickUpEnabled
                ? renderCartLinesGroup(
                      supersessionData,
                      handleInventoryLinkClick,
                      advancedNotes || [],
                      isPrintView,
                      props,
                      props.resources,
                      pagination,
                      paginatedView,
                      isAlternateWarehouseSelectionDisabled,
                      shouldDisplayAlternateWarehouseFeature,
                      warehouseStateMap,
                      props.context.app.config
                      //   enforcePrimaryWarehouseAvailability
                      //   hasWarehouseChanged
                  )
                : renderCartlines(
                      supersessionData,
                      handleInventoryLinkClick,
                      advancedNotes || [],
                      isPrintView,
                      props,
                      props.cartlines,
                      props.resources,
                      props.storeSelector,
                      props.waitingComponent,
                      props.cartLoadingStatus,
                      props.cartDataResult,
                      pagination,
                      paginatedView,
                      isAlternateWarehouseSelectionDisabled,
                      shouldDisplayAlternateWarehouseFeature,
                      warehouseStateMap,
                      props.context.app.config
                      //   enforcePrimaryWarehouseAvailability
                      //   hasWarehouseChanged
                  )}
            {props.orderSummaryHeading && (
                <Node {...props.OrderSummaryWrapper}>
                    {printViewToggle}
                    <Node className='msc-order-summary__items'>
                        {props.orderSummaryHeading}
                        {props.cart?.hasInvoiceLine
                            ? renderInvoiceSummarylines(invoiceSummaryLineitems, props.OrderSummaryItems, props)
                            : renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}

                        {renderErrorBlock(props.OrderSummaryErrors)}
                        <RenderCreditBalanceMessage props={props} />
                    </Node>
                    {shouldRenderReminder &&
                        couponReminderMessage &&
                        couponReminderMessage.trim() &&
                        !props.cart?.hasInvoiceLine &&
                        renderReminderMessage(props)}

                    <RenderOrderSummaryCheckout
                        props={props}
                        renderClearCart={renderClearCart}
                        shouldDisplaySpringOrderModal={shouldDisplaySpringOrderModal}
                    />
                </Node>
            )}

            {props.storeSelector}
            {_renderClearCartModal(handleClearCart, setClearCartModalActive, toggle, clearCartModalActive, props.resources)}
            {renderAvailabilityModal()}
        </div>
    );
};

export default CartView;
